import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, Home as HomeIcon, Search, ShoppingBag, User } from 'lucide-react';
import { API_URL } from '../utils/cartApi';

const safeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '/placeholder.png';
  const blocked = /(localhost|127\.0\.0\.1|:7071)/i;
  return blocked.test(url) ? '/placeholder.png' : url;
};

const optimizeCloudinaryImage = (url) => {
  const safeUrl = safeImageUrl(url);

  if (safeUrl.includes('res.cloudinary.com') && safeUrl.includes('/upload/')) {
    return safeUrl.replace('/upload/', '/upload/w_400,q_auto,f_auto/');
  }

  return safeUrl;
};

const Explore = () => {
  const [allUniqueProducts, setAllUniqueProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [wishlist, setWishlist] = useState(() => {
    try {
      const savedWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      return Array.isArray(savedWishlist) ? savedWishlist : [];
    } catch (error) {
      return [];
    }
  });

  const navigate = useNavigate();
  const location = useLocation();

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products/all`);

      const fetchedProducts = Array.isArray(res.data) ? res.data : (res.data.products || []);

      const uniqueProducts = [];
      const seenParents = new Set();

      fetchedProducts.forEach(product => {
        const pId = product.parentSareeId || product.parentsareeId;
        const normalizedId = pId !== undefined && pId !== null ? String(pId).trim() : "";

        if (
          !normalizedId ||
          normalizedId.toUpperCase() === "N/A" ||
          normalizedId.toLowerCase() === "undefined"
        ) {
          uniqueProducts.push(product);
        } else if (!seenParents.has(normalizedId)) {
          seenParents.add(normalizedId);
          uniqueProducts.push(product);
        }
      });

      console.log(
        "🛠️ DEBUG - EXPLORE PAGE: Raw Products:",
        fetchedProducts.length,
        "| Unique Filtered:",
        uniqueProducts.length
      );

      setAllUniqueProducts(uniqueProducts);
    } catch (err) {
      console.error("Error fetching sarees:", err);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const categories = ['All', 'Mul Cotton', 'Khadi Cotton', 'Celebrity inspired Sarees', 'Maheshwari Silk Sarees'];

  const displayedProducts = allUniqueProducts.filter(product => {
    if (selectedCategory === 'All') return true;
    const searchString = `${product.name || ''} ${product.category || ''} ${product.description || ''}`.toLowerCase();
    return searchString.includes(selectedCategory.toLowerCase());
  });

  const toggleFilterMenu = () => setShowFilterMenu(prev => !prev);

  const selectCategory = (category) => {
    setSelectedCategory(category);
    setShowFilterMenu(false);
  };

  const toggleWishlist = (productId) => {
    setWishlist((currentWishlist) => {
      const nextWishlist = currentWishlist.includes(productId)
        ? currentWishlist.filter((id) => id !== productId)
        : [...currentWishlist, productId];

      localStorage.setItem('wishlist', JSON.stringify(nextWishlist));
      window.dispatchEvent(new Event('wishlistUpdated'));
      return nextWishlist;
    });
  };

  return (
    <div className="min-h-screen bg-cover bg-repeat pb-24 font-sans md:pb-0" style={{ backgroundImage: "url('/bg-parchment.jpg')" }}>
      <div className="relative">
        <div className="fixed left-4 bottom-24 z-50">
          <button
            onClick={toggleFilterMenu}
            className="bg-[#3d0c11] text-white uppercase text-[10px] tracking-widest font-bold px-4 py-3 rounded-sm shadow-lg"
          >
            {showFilterMenu ? 'Hide Filters' : 'Filters'}
          </button>
          {showFilterMenu && (
            <div className="mt-3 w-56 bg-white border border-gray-200 rounded shadow-lg p-4 space-y-3">
              <h3 className="text-xs uppercase tracking-[0.2em] text-gray-500">Category</h3>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => selectCategory(category)}
                  className={`w-full text-left text-sm px-3 py-2 rounded ${selectedCategory === category ? 'bg-[#3d0c11] text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}
        </div>

        <main className="flex-1 p-4 md:p-16">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-8">
            {displayedProducts.map(saree => (
              <div
                key={saree._id}
                className="group bg-white p-2 md:p-4 shadow-md hover:shadow-2xl transition-all duration-500 rounded-sm flex flex-col"
              >
                <div
                  className="aspect-[3/4] overflow-hidden relative cursor-pointer"
                  onClick={() => navigate(`/product/${saree._id}`)}
                >
                  <img
                    src={optimizeCloudinaryImage(saree.images?.[0])}
                    alt={saree.name}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.src = "/placeholder.png";
                    }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <button
                    type="button"
                    aria-label={wishlist.includes(saree._id) ? 'Remove from Wishlist' : 'Add to Wishlist'}
                    aria-pressed={wishlist.includes(saree._id)}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleWishlist(saree._id);
                    }}
                    className="absolute top-3 right-3 z-10 rounded-full bg-white/85 p-2 text-[#1A1A1A] shadow-sm backdrop-blur-sm hover:text-rose-600"
                  >
                    <Heart size={16} fill={wishlist.includes(saree._id) ? 'currentColor' : 'none'} />
                  </button>
                </div>

                <div className="mt-4 text-center flex-grow">
                  <h4 className="text-[#3d0c11] font-bold text-[9px] md:text-[11px] uppercase tracking-widest mb-1 truncate px-1">
                    {saree.name}
                  </h4>
                  <p className="text-[#8c7b70] font-serif italic font-bold text-sm md:text-lg">
                    ₹ {saree.price.toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </main>
      </div>

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 py-4 px-8 flex justify-between items-center z-[90] shadow-[0_-5px_15px_rgba(0,0,0,0.05)] md:hidden">
        <button onClick={() => navigate('/')} className={`flex flex-col items-center gap-1 ${location.pathname === '/' ? 'text-[#3d0c11]' : 'text-gray-400'}`}>
          <HomeIcon size={20} />
          <span className="text-[9px] font-bold uppercase tracking-tighter">Home</span>
        </button>

        <button onClick={() => navigate('/explore')} className={`flex flex-col items-center gap-1 ${location.pathname === '/explore' ? 'text-[#3d0c11]' : 'text-gray-400'}`}>
          <Search size={20} />
          <span className="text-[9px] font-bold uppercase tracking-tighter">Explore</span>
        </button>

        <button onClick={() => navigate('/cart')} className={`flex flex-col items-center gap-1 ${location.pathname === '/cart' ? 'text-[#3d0c11]' : 'text-gray-400'}`}>
          <ShoppingBag size={20} />
          <span className="text-[9px] font-bold uppercase tracking-tighter">Cart</span>
        </button>

        <button onClick={() => navigate('/login')} className={`flex flex-col items-center gap-1 ${location.pathname === '/login' ? 'text-[#3d0c11]' : 'text-gray-400'}`}>
          <User size={20} />
          <span className="text-[9px] font-bold uppercase tracking-tighter">Login</span>
        </button>
      </nav>
    </div>
  );
};

export default Explore;